import { type ReactNode } from "react";

interface FeatureBlockProps {
  className?: string;
  /** Feature title */
  title: string;
  /** Feature description */
  description: string;
  /** Title position relative to illustration (only for layout="default") */
  titlePosition?: "top" | "bottom";
  /** Illustration — mockup, screenshot, preview etc */
  children?: ReactNode;
  /**
   * Layout variant:
   * - "default" (default): text absolute-positioned, illustration free-floating
   * - "side": flex row — text flex-1 min-w-0 (wraps naturally), illustration flex-none on the right
   */
  layout?: "default" | "side";
  /** Narrows text to 310px on tablet so right-side illustrations don't overlap */
  narrowText?: boolean;
  /**
   * Scoped narrow-breakpoint (<1269) typography matching the «Возможности» Figma frame
   * (the Figma tablet 769 frame equals the mobile frame): title 32px/40 (-0.64px),
   * description 18px/24 (-0.36px) at rgba(0,0,0,0.8). Desktop (≥1269) stays untouched.
   */
  mobileFigmaText?: boolean;
  /** Optional icon (40×40) shown left of the title in the "side" layout. */
  icon?: ReactNode;
  /**
   * Keeps the card at its className height across the whole narrow range (<1269) instead
   * of the default `!h-auto` collapse. Used for default-layout cards that pin their
   * illustration to the bottom of a fixed-height card (e.g. «Транскрибация по ссылке»).
   */
  mobileFixedHeight?: boolean;
  /**
   * Fixes the card to a specific Figma height across the narrow range (<1269), overriding
   * the default `!h-auto` collapse. The illustration overflows and is cropped by the card's
   * `overflow-hidden` edge; text stays fully visible. Desktop (≥1269) stays untouched.
   * Provide a Tailwind height class with `!`, e.g. `max-desktop:!h-[476px]`.
   */
  mobileHeightClass?: string;
  /**
   * Chat-specific layout for «ИИ чат» (Figma node 1423:40090) on the narrow range (<1269):
   * title + description pinned to the BOTTOM of the fixed-height card, illustration
   * absolutely anchored just above the text so its TOP overflows and is cropped by the
   * card's top edge. Only the bottom of the chat (input + last messages) stays visible.
   * No overlap with the text. Desktop (≥1269) untouched.
   */
  mobileChatLayout?: boolean;
}

/** Feature block for «Возможности» section — covers both 632px and 1280px wide variants */
export function FeatureBlock({
  className = "",
  title,
  description,
  titlePosition = "top",
  children,
  layout = "default",
  narrowText = false,
  mobileFigmaText = false,
  icon,
  mobileFixedHeight = false,
  mobileHeightClass = "",
  mobileChatLayout = false,
}: FeatureBlockProps) {
  // Scoped narrow-breakpoint text overrides — keep desktop (≥1269) classes intact.
  // mobileFigmaText cards (Telegram-бот, Транскрибация по ссылке) use the Figma mobile
  // typography across the whole <1269 range, because the Figma tablet (769) frame matches
  // the mobile frame. The non-Figma title/desc shrink only on mobile (<769) as before.
  const titleMobile = mobileFigmaText
    ? "max-desktop:text-[32px] max-desktop:leading-[40px] max-desktop:tracking-[-0.64px]"
    : "max-tablet:text-[24px] max-tablet:leading-[32px] max-tablet:tracking-[-0.48px]";
  const descMobile = mobileFigmaText
    ? "max-desktop:text-[18px] max-desktop:leading-[24px] max-desktop:text-[rgba(0,0,0,0.8)]"
    : "max-tablet:text-[16px] max-tablet:leading-[20px]";
  if (layout === "side") {
    return (
      <div
        className={`relative bg-white rounded-[16px] max-w-full overflow-hidden hover:shadow-[0px_0px_10px_rgba(0,0,0,0.07)] transition-shadow flex items-stretch max-desktop:flex-col max-desktop:items-stretch ${className}`}
      >
        {/* Text — flex-1 min-w-0 so it wraps naturally and never overlaps illustration.
            On narrow breakpoints (<1269) it stops flex-growing so the phone mock-up sits below it. */}
        <div className="flex-1 min-w-0 max-desktop:flex-none flex flex-col gap-[16px] p-[40px] z-10">
          {icon ? (
            <div className="flex items-center gap-[16px]">
              <span className="size-[40px] shrink-0 flex items-center justify-center max-desktop:flex hidden">
                {icon}
              </span>
              <h3
                className={`[font-family:var(--font-commissioner)] font-bold not-italic text-[32px] leading-[40px] text-black tracking-[-0.64px] ${titleMobile}`}
                style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
              >
                {title}
              </h3>
            </div>
          ) : (
            <h3
              className={`[font-family:var(--font-commissioner)] font-bold not-italic text-[32px] leading-[40px] text-black tracking-[-0.64px] ${titleMobile}`}
              style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
            >
              {title}
            </h3>
          )}
          <p
            className={`[font-family:var(--font-commissioner)] font-medium not-italic text-[18px] leading-[24px] text-text-subtle tracking-[-0.36px] ${descMobile}`}
            style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          >
            {description}
          </p>
        </div>

        {/* Illustration slot — flex-none sized box on desktop ≥1269 (illustration overflows bottom);
            on narrow breakpoints (<1269) a full-width relative block below the text, illustration
            centered + cropped by the card. */}
        <div className="flex-none relative self-stretch w-[295px] max-desktop:w-full max-desktop:flex-1 max-desktop:self-auto">
          {children}
        </div>
      </div>
    );
  }

  // Cards that pin a fixed Figma height across the narrow range must NOT also get the
  // `!h-auto` collapse — two equal-specificity `max-desktop:!h-*` rules would fight and the
  // result is order-dependent. So gate `!h-auto` to cards without an explicit narrow height.
  const hasFixedNarrowHeight = mobileFixedHeight || mobileHeightClass.includes("h-[");
  return (
    <div
      className={`relative bg-white rounded-[16px] max-w-full overflow-hidden hover:shadow-[0px_0px_10px_rgba(0,0,0,0.07)] transition-shadow ${
        hasFixedNarrowHeight ? "" : "max-desktop:!h-auto"
      } ${
        // Chat uses a block positioning context (text pinned, mock-up cropped); the rest flow
        // as a column on narrow breakpoints.
        mobileChatLayout ? "" : "max-desktop:flex max-desktop:flex-col"
      } ${
        // titlePosition "bottom" ⇒ illustration first, text after → reverse the column.
        titlePosition === "bottom" && !mobileChatLayout ? "max-desktop:flex-col-reverse" : ""
      } ${
        // Re-fix the card height on all narrow breakpoints (<1269).
        mobileFixedHeight ? "max-desktop:!h-[400px]" : ""
      } ${
        // Explicit Figma height for the narrow range.
        mobileHeightClass
      } ${
        // Chat layout: the card must be a positioning context on <1269 so the text can
        // pin to the bottom and the chat mock-up can anchor above it.
        mobileChatLayout ? "max-desktop:!flex-none max-desktop:relative max-desktop:block" : ""
      } ${className}`}
    >
      {/* Text block — absolute on desktop, normal flow on max-desktop (no overlap) */}
      <div
        className={`absolute z-10 left-[40px] max-tablet:left-[24px] flex flex-col gap-[16px] max-tablet:gap-[12px] max-desktop:static max-desktop:left-auto max-desktop:p-[40px] max-tablet:p-[24px] ${
          narrowText
            ? "w-[450px] max-desktop:w-full max-tablet:w-full"
            : "w-[450px] max-w-[calc(100%-80px)] max-desktop:max-w-full max-desktop:w-full max-tablet:max-w-full max-tablet:w-full"
        } ${
          titlePosition === "top" ? "top-[40px] max-tablet:top-[24px]" : "bottom-[40px] max-desktop:bottom-auto max-desktop:top-auto max-tablet:top-auto max-tablet:bottom-auto"
        } ${
          // Figma narrow card (<1269): 40px padding + 16px gap, text does not grow so the
          // illustration slot below can take the remaining height and pin to the bottom.
          mobileFixedHeight ? "max-desktop:!p-[40px] max-desktop:gap-[16px] max-desktop:flex-none" : ""
        } ${
          // Chat layout (Figma 1423:40090): pin title+description to the bottom of the
          // fixed-height card. The chat mock-up anchors above and overflows the top edge.
          mobileChatLayout
            ? "max-desktop:!absolute max-desktop:!bottom-0 max-desktop:!top-auto max-desktop:left-0 max-desktop:right-0 max-desktop:!w-full max-desktop:!p-[24px] max-desktop:gap-[16px] max-desktop:z-20"
            : ""
        }`}
      >
        <h3
          className={`[font-family:var(--font-commissioner)] font-bold not-italic text-[32px] leading-[40px] text-black tracking-[-0.64px] w-full ${titleMobile}`}
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {title}
        </h3>
        <p
          className={`[font-family:var(--font-commissioner)] font-medium not-italic text-[18px] leading-[24px] text-text-subtle tracking-[-0.36px] w-full ${descMobile}`}
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {description}
        </p>
      </div>

      {/* Illustration slot — `contents` on desktop (children stay absolute-to-card → zero desktop change),
          a sized relative box on max-desktop so the illustration's internal absolutes flow as a column block.
          With mobileFixedHeight the slot becomes a flex column on mobile so the illustration can pin to the
          bottom of the fixed-height card. */}
      <div
        className={`contents max-desktop:block max-desktop:relative max-desktop:w-full max-desktop:flex-1 ${
          mobileFixedHeight ? "max-desktop:flex max-desktop:flex-col max-desktop:justify-end" : ""
        } ${
          // Chat layout: slot is not a positioning context so the chat mock-up's
          // absolute anchoring resolves against the card (cropped by the card top edge).
          mobileChatLayout ? "max-desktop:!static max-desktop:!block" : ""
        }`}
      >
        {children}
      </div>
    </div>
  );
}
